import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import axios, { Endpoint, formatEndpoint } from 'src/utils/axios';
import { BackendValidationError } from 'src/utils/backend-validation-error';
import { deepCopy } from 'src/utils/object-utils';
import { useRouter } from 'src/routes/hooks';
import { useAuthContext } from 'src/auth/hooks';
import { HasId } from './use-model-data';

export type ModelSavingData<T> = {
  loading: boolean;
  errors: any[];
  clearErrors: VoidFunction;
  save: () => Promise<AxiosResponse<any, any> | null>;
  destroy: () => Promise<AxiosResponse<any, any> | null>;
  set: (t: T) => void;
  update: (updater: (t: T) => void) => void;
  model?: T;
  isNew: boolean;
};

type ModalSavingState<T> = {
  loading: boolean;
  errors: BackendValidationError[];
  model?: T;
};

export default function useModelSavingData<T>(
  putEndpoint: Endpoint | null,
  postEndpoint?: Endpoint,
  deleteEndpoint?: Endpoint
): ModelSavingData<T> {
  const [state, setState] = useState<ModalSavingState<T>>({
    loading: false,
    errors: [],
  } as ModalSavingState<T>);

  const router = useRouter();
  const { logout } = useAuthContext();

  const set = (newSelected: T) => setState({ ...state, model: deepCopy(newSelected) });
  const update = (updater: (t: T) => void) => {
    updater(state.model!);
    setState((s) => ({ ...s }));
  };

  const destroy = async (): Promise<AxiosResponse<any, any> | null> => {
    if (!state.model) {
      return null;
    }
    const model = state.model as any;
    if (model.id <= 0 || !deleteEndpoint) {
      return null;
    }

    setState((s) => ({ ...s, loading: true }));

    try {
      const response = await axios.delete(formatEndpoint(deleteEndpoint, state.model));

      setState((s) => ({ ...s, loading: false }));

      return response;
    } catch (error) {
      setState((s) => ({ ...s, loading: false }));
      if (error?.response?.status === 422) {
        const errorsLocal: BackendValidationError[] = [];
        error.response.data.forEach((e: any) => {
          errorsLocal.push(e);
        });
        setState((s) => ({ ...s, errors: errorsLocal }));
      }
      throw error;
    }
  };

  const save = async (): Promise<AxiosResponse<any, any> | null> => {
    if (!state.model) {
      return null;
    }
    setState((s) => ({ ...s, loading: true }));

    try {
      const model = state.model as any;

      const response =
        !!model.id && putEndpoint
          ? await axios.put(formatEndpoint(putEndpoint, state.model), state.model)
          : await axios.post(formatEndpoint(postEndpoint as any, state.model), state.model);

      setState((s) => ({ ...s, loading: false }));

      return response;
    } catch (error) {
      setState((s) => ({ ...s, loading: false }));

      if (error?.response?.status === 401) {
        logout();
        router.push('/auth/login');
      } else if (error?.response?.status === 422) {
        const errorsLocal: BackendValidationError[] = [];
        error.response.data.forEach((e: any) => {
          errorsLocal.push(e);
        });
        setState((s) => ({ ...s, errors: errorsLocal }));
      } else if (error?.response?.status === 403) {
        enqueueSnackbar('You are not allowed to perform that action', { variant: 'warning' });
      } else if (error?.response?.status === 500) {
        enqueueSnackbar('An unexpected error has occured', { variant: 'error' });
      } else {
        // we probably don't have wifi
        enqueueSnackbar('An unexpected error has occured', { variant: 'error' });
      }
      throw error;
    }
  };
  useEffect(() => {
    setState((s) => ({ ...s, loading: false, errors: [] }));
  }, [state.model]);

  const isNew = (state.model && ((state.model as any).id === 0 || (state.model as any).id === "")) || false;

  const clearErrors = () => {
    setState((s) => ({ ...s, errors: [] }));
  };

  return {
    loading: state.loading,
    errors: state.errors,
    clearErrors,
    save,
    destroy,
    set,
    update,
    model: state.model,
    isNew,
  };
}
